import React, { useMemo } from 'react';
import { formatReportDepth } from '../../../utils/dailyReport/drillingInProgressForReport';
import {
  normalizeQuicklogIntervals,
  plainObjectToListVisualMaps,
  resolveListVisual,
} from '../../../utils/drillz/listValueVisuals';
import QuicklogRockSwatch from './QuicklogRockSwatch';

const SWATCH_COLUMNS = [
  { key: 'rock_type', listCode: 'rock_type', label: 'Rock Type' },
  { key: 'total_min_pct', listCode: null, label: 'Total Min' },
  { key: 'min_style', listCode: 'min_style', label: 'Min Style' },
  { key: 'min_type', listCode: 'min_type', label: 'Cu Min' },
  { key: 'comment', listCode: null, label: 'Comments' },
];

function cellText(row, key) {
  if (key === 'depth') {
    return `${formatReportDepth(row.from_depth)}–${formatReportDepth(row.to_depth)}`;
  }
  const value = row?.[key];
  if (value == null || value === '') return '—';
  const text = String(value).trim();
  if (!text || text.toLowerCase() === 'nan') return '—';
  return text;
}

const QuicklogIntervalTable = ({
  quicklogRows = [],
  listVisualMaps = null,
  variant = 'screen',
  rowHeightPx = null,
  gridColumns = null,
}) => {
  const intervals = useMemo(() => normalizeQuicklogIntervals(quicklogRows), [quicklogRows]);
  const maps = useMemo(
    () =>
      listVisualMaps instanceof Map
        ? listVisualMaps
        : plainObjectToListVisualMaps(listVisualMaps),
    [listVisualMaps]
  );

  const isPrint = variant === 'print';
  const resolvedRowHeight = rowHeightPx ?? (isPrint ? 18 : 22);
  const resolvedGrid =
    gridColumns ??
    (isPrint
      ? '62px 54px 46px 54px 80px minmax(0, 1fr)'
      : '72px 1fr 64px 1fr 1fr minmax(0, 2fr)');

  const headerStyle = {
    padding: isPrint ? '4px 4px' : '6px 8px',
    fontSize: isPrint ? '7.5px' : '10px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.02em',
    color: isPrint ? '#374151' : '#9ca3af',
    borderLeft: isPrint ? '1px solid #e5e7eb' : '1px solid #374151',
    background: isPrint ? '#f8fafc' : '#111827',
  };

  const bodyStyle = {
    padding: isPrint ? '2px 4px' : '4px 6px',
    fontSize: isPrint ? '8px' : '11px',
    color: isPrint ? '#111827' : '#f3f4f6',
    borderLeft: isPrint ? '1px solid #e5e7eb' : '1px solid #374151',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    minHeight: 0,
    overflow: 'hidden',
  };

  const renderValueCell = (row, col) => {
    if (!col.listCode) {
      return (
        <span
          style={{
            wordBreak: col.key === 'comment' ? 'break-word' : 'normal',
            whiteSpace: col.key === 'comment' ? 'normal' : 'nowrap',
            textOverflow: 'ellipsis',
          }}
        >
          {cellText(row, col.key)}
        </span>
      );
    }

    const listMap = maps.get(col.listCode);
    const raw = row[col.key];
    const visual = resolveListVisual(listMap, raw);
    const text = cellText(row, col.key);

    return (
      <>
        <QuicklogRockSwatch
          visual={visual}
          sizePx={isPrint ? 10 : 14}
          useTextureImg={isPrint}
        />
        <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{text}</span>
      </>
    );
  };

  if (!intervals.length) {
    return (
      <div
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '16px',
          color: isPrint ? '#6b7280' : '#9ca3af',
          fontSize: isPrint ? '11px' : '13px',
        }}
      >
        No quicklog intervals available
      </div>
    );
  }

  const columns = [{ key: 'depth', label: 'Depth (m)', listCode: null }, ...SWATCH_COLUMNS];

  return (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
      }}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: resolvedGrid,
          borderBottom: isPrint ? '1px solid #e5e7eb' : '1px solid #374151',
          flexShrink: 0,
        }}
      >
        {columns.map((col, index) => (
          <div
            key={col.key}
            style={{
              ...headerStyle,
              borderLeft: index === 0 ? 'none' : headerStyle.borderLeft,
            }}
          >
            {col.label}
          </div>
        ))}
      </div>

      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: isPrint ? 'hidden' : 'auto',
        }}
      >
        {intervals.map((row, index) => (
          <div
            key={`${row.from_depth}-${row.to_depth}-${index}`}
            style={{
              flex: 'none',
              height: `${resolvedRowHeight}px`,
              display: 'grid',
              gridTemplateColumns: resolvedGrid,
              borderBottom:
                index < intervals.length - 1
                  ? isPrint
                    ? '1px solid #e5e7eb'
                    : '1px solid #374151'
                  : 'none',
              background: isPrint
                ? index % 2 === 0
                  ? '#ffffff'
                  : '#f8fafc'
                : index % 2 === 0
                  ? '#030712'
                  : '#111827',
              boxSizing: 'border-box',
            }}
          >
            {columns.map((col, colIndex) => (
              <div
                key={col.key}
                style={{
                  ...bodyStyle,
                  borderLeft: colIndex === 0 ? 'none' : bodyStyle.borderLeft,
                  color:
                    col.key === 'depth'
                      ? isPrint
                        ? '#374151'
                        : '#9ca3af'
                      : bodyStyle.color,
                }}
              >
                {col.key === 'depth' ? cellText(row, 'depth') : renderValueCell(row, col)}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

export default QuicklogIntervalTable;
